import store from '@/store';
import _ from 'lodash';
import { publicRoutes, navigateRoutes, priorityRoutes } from '@/router/constants';
import { Route, NavigationGuardNext } from 'vue-router';

const rules = [
  {match: '/v1/profile/auth', method: 'post', auth: false, role: ['All']},
  {
    match: '/v1/record/filter',
    auth: true,
    access: [{
      role: ['All']
    },
    {role: ['All']}]
  }
];

export const navigate = (to: Route, from: Route, next: NavigationGuardNext) => {
  const roles = store.getters['login/roles'];

  // Public routes check
  if (to.name && (publicRoutes as string[]).includes(to.name)) {
    next();
    return;
  }

  // Auth check
  if (!store.getters['login/isLoggedIn']) {
    if (to.name === 'login') {
      next();
      return;
    }
    store.dispatch('general/setLayout', 'Login');
    next({ name: 'login' });
    return;
  }

  try {
    // Admin check
    if (_.some(roles, {'manager': true})) {
      next();
      return;
    }

    // Si el dominio es 'All', permitir acceso
    if (to.meta?.domain === 'All') {
      next();
      return;
    }

    // Añadir logs para depuración
    console.log('Checking access for route:', to.name);
    console.log('Route domain:', to.meta?.domain);
    console.log('User roles:', roles);

    // Domain permission check - Mejorado para manejar rutas anidadas
    const hasAccess = roles.some(role => {
      // Si la ruta es hija, usar el dominio del padre
      const routeDomain = to.meta?.parent ? 
        to.matched[0].meta?.domain : 
        to.meta?.domain;

      console.log('Checking domain:', routeDomain);
      
      const domainConfig = role.domains?.find(d => d.domain === routeDomain);
      console.log('Domain config found:', domainConfig);
      
      return domainConfig?.permissions.includes('Read');
    });

    console.log('Has access:', hasAccess);

    if (hasAccess) {
      next();
      return;
    }

    // Si no tiene acceso directo y es una ruta hija, intentar con la ruta padre
    if (to.matched.length > 1) {
      const parentRoute = to.matched[0];
      const hasParentAccess = roles.some(role => {
        const domainConfig = role.domains?.find(d => d.domain === parentRoute.meta?.domain);
        return domainConfig?.permissions.includes('Read');
      });

      if (hasParentAccess) {
        next();
        return;
      }
    }

    // Find alternative route - solo si no es una ruta anidada
    if (to.matched.length <= 1) {
      const accessibleRoute = navigateRoutes.find(route => {
        return roles.some(role => {
          const domainConfig = role.domains?.find(d => d.domain === route.domain);
          return domainConfig?.permissions.includes('Read');
        });
      });

      if (accessibleRoute) {
        next({ name: accessibleRoute.name });
        return;
      }
    }

    // Si es una ruta personalizada, verificar solo el dominio
    if (to.meta?.isCustomRoute) {
      const hasAccess = roles.some(role => {
        return role.domains?.some(d => 
          d.domain === to.meta?.domain && 
          d.permissions.includes('Read')
        );
      });

      if (hasAccess) {
        next();
        return;
      }
    }

    // No access, redirect to dashboard
    next({ name: 'dashboard' });

  } catch (e) {
    console.error('Navigation error:', e);
    next({ name: 'dashboard' });
  }
};

// Helper function to find the first accessible route for a user based on their roles
export const findFirstAccessibleRoute = (roles: any[]): string => {
  // First check priority routes (defined in constants.ts)
  for (const route of priorityRoutes) {
    const hasAccess = roles.some(role => {
      const domainConfig = role.domains?.find(d => d.domain === route.domain);
      return domainConfig?.permissions.includes('Read');
    });
    
    if (hasAccess) {
      return route.name;
    }
  }

  // If no priority route is accessible, check all routes
  for (const route of navigateRoutes) {
    const hasAccess = roles.some(role => {
      const domainConfig = role.domains?.find(d => d.domain === route.domain);
      return domainConfig?.permissions.includes('Read');
    });
    
    if (hasAccess) {
      return route.name;
    }
  }

  // If no specific route is found, default to dashboard
  return 'dashboard';
};

export const redirectBasedOnIntegration = (to: Route, from: Route, next: NavigationGuardNext) => {
  const profile = JSON.parse(localStorage.getItem('profile') || '{}');
  const roles = store.getters['login/roles'];
  
  // Add debugging to see what's happening
  console.log('redirectBasedOnIntegration called');
  console.log('Current route:', to.name, to.path);
  console.log('Profile exists:', !!profile?.id);
  console.log('User roles:', roles);
  console.log('Is manager:', _.some(roles, {'manager': true}));

  // PRIORITARIO: Si el usuario está autenticado y está intentando acceder al login, redirigirlo
  if (to.name === 'login' && profile?.id && store.getters['login/isLoggedIn']) {
    console.log('User is authenticated and trying to access login page, redirecting...');
    
    if (_.some(roles, {'manager': true})) {
      console.log('User is manager, redirecting to dashboard');
      next({ name: 'dashboard' });
      return;
    }
    
    console.log('Finding first accessible route for regular user');
    const firstAccessibleRoute = findFirstAccessibleRoute(roles);
    console.log('Redirecting to:', firstAccessibleRoute);
    next({ name: firstAccessibleRoute });
    return;
  }

  // Si estamos intentando acceder a una ruta personalizada, permitirlo
  if (to.meta?.isCustomRoute) {
    next();
    return;
  }

  // Si estamos yendo a '/' y tenemos perfil, buscar primera ruta con acceso
  if (to.path === '/' && profile?.id) {
    if (_.some(roles, {'manager': true})) {
      next({ name: 'dashboard' });
      return;
    }

    const firstAccessibleRoute = findFirstAccessibleRoute(roles);
    next({ name: firstAccessibleRoute });
    return;
  }

  // Si no tenemos perfil, ir a login
  if (!profile?.id) {
    next({ name: 'login' });
    return;
  }

  next();
};

export const request = (config: any, token: string) => {
  const path = rules.find(f => config.url.match(f.match));
  if (!path) return true;
  if (!path.auth) return false;
  if (!token) return true;

  const roles = store.getters['login/roles'];

  if (path.hasOwnProperty('method')) {
    if (path.method === config.method) {
      if (path.role[0] === 'All') {
        return false;
      }
      return !roles.find(role => 
        path.role.some(f => `ROLE_${f}` === role)
      );
    }
    return true;
  }

  const methodRoles = config.method === 'get' ? 
    path.access[0].role : 
    path.access[1].role;

  return !roles.find(role => 
    methodRoles.some(f => `ROLE_${f}` === role)
  );
};

export default { navigate, request, redirectBasedOnIntegration, findFirstAccessibleRoute };
